// ─────────────────────────────────────────────────────────
// layouts/nav/NavFlyout.tsx — 248px rich flyout panel
//
// Four zones (top → bottom):
//   1. Header — title + subtitle + unpin button
//   2. Modules — NavLink rows with icon chips + badges
//   3. Pinned records — smart auto-surface + manual pins
//   4. Quick add — 2-col grid (mt-auto, pinned to bottom)
//
// Recent zone deferred to follow-up (empty state shown).
// ─────────────────────────────────────────────────────────
import { useMemo }    from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore }         from '@/store/authStore'
import { useDataStore }         from '@/store/dataStore'
import { useUIStore }           from '@/store/uiStore'
import { checkDep }             from '@/utils/deps'
import type { DepData }         from '@/utils/deps'
import {
  MODULE_ICONS,
  getAutoSurfaced,
  getManualPinIds,
  countOverdueTasks,
  countDelayedMilestones,
  countPendingReceivables,
  countPendingPOs,
  countUnmatchedRecon,
  countPendingSalaries,
} from './navData'
import type { NavCategory, SBIconDef, PinnedRecord } from './navData'

// ── Inline SVG renderer ───────────────────────────────────
function SBIcon({ def, size }: { def: SBIconDef; size: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16"
      fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      {def.complex
        ? <g dangerouslySetInnerHTML={{ __html: def.complex }} />
        : <>
            {def.d     && <path d={def.d} />}
            {def.extra && <path d={def.extra} />}
          </>
      }
    </svg>
  )
}

// ── Section label ─────────────────────────────────────────
function SectionLabel({ children, action }: {
  children: React.ReactNode
  action?:  React.ReactNode
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 10px 6px',
    }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600,
        color: 'var(--color-sb-ac)', opacity: .9,
        letterSpacing: '.14em', textTransform: 'uppercase',
      }}>{children}</span>
      {action}
    </div>
  )
}

// ── Module badge pill ─────────────────────────────────────
function ModBadge({ n }: { n: number }) {
  if (!n) return null
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
      padding: '2px 7px', borderRadius: 99,
      background: 'rgba(0,216,208,.16)', color: 'var(--color-sb-ac)',
      letterSpacing: '.04em', flexShrink: 0,
    }}>{n > 99 ? '99+' : n}</span>
  )
}

interface NavFlyoutProps {
  category:     NavCategory
  activeRoute:  string
  onClose:      () => void   // collapses flyout to rail-only (desktop)
  onLinkClick?: () => void   // called after any NavLink tap (closes mobile drawer)
  badges:       Record<string, number>
  companyId:    string
}

export default function NavFlyout({
  category,
  activeRoute,
  onClose,
  onLinkClick,
  badges,
  companyId,
}: NavFlyoutProps) {
  const { isMasterAdmin } = useAuthStore()
  const navigate = useNavigate()
  const { showToast } = useUIStore()
  const { proj, recv, exp, po, emp, tds, vendor, task, invoice, tender, milestones, recon, sal } = useDataStore()

  const depData: DepData = {
    proj, recv, exp, vendor, emp, task, invoice, tender,
    emd: [], att: {},
  }

  // ── Pinned records ───────────────────────────────────────
  const autoSurfaced: PinnedRecord[] = useMemo(
    () => getAutoSurfaced(category.id, { proj, recv, exp, po, emp, tds, vendor }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category.id, proj.length, recv.length, exp.length, po.length, emp.length, tds.length]
  )

  const manualPinIds = useMemo(
    () => getManualPinIds(companyId, category.id),
    [companyId, category.id]
  )

  // Manual pins resolved to PinnedRecord (those that still exist in auto pool)
  const allRecordsMap = useMemo(() => {
    const pool = getAutoSurfaced(category.id, { proj, recv, exp, po, emp, tds, vendor }, 100)
    return Object.fromEntries(pool.map(r => [r.id, r]))
  }, [category.id, proj, recv, exp, po, emp, tds, vendor])

  const manualRecords: PinnedRecord[] = manualPinIds
    .map(id => allRecordsMap[id])
    .filter((r): r is PinnedRecord => !!r)

  // De-duped: manual first, then auto (not already pinned), capped at 5
  const pinnedIds = new Set(manualRecords.map(r => r.id))
  const combined: PinnedRecord[] = [
    ...manualRecords,
    ...autoSurfaced.filter(r => !pinnedIds.has(r.id)),
  ].slice(0, 5)

  // ── Per-module badge counts ──────────────────────────────
  const moduleBadge = useMemo<Record<string, number>>(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0)
    void today  // used inside helpers
    return {
      planner:     countOverdueTasks(task),
      milestones:  countDelayedMilestones(milestones),
      receivables: countPendingReceivables(recv),
      po:          countPendingPOs(po),
      bankrecon:   countUnmatchedRecon(recon),
      salary:      countPendingSalaries(sal),
    }
  }, [task, milestones, recv, po, recon, sal])

  // ── Subtitle: "N modules · X need you" ──────────────────
  const visibleModules = category.modules.filter(m => !m.maOnly || isMasterAdmin())
  const needsAttention = badges[category.id] ?? 0
  const subtitle = needsAttention > 0
    ? `${visibleModules.length} modules · ${needsAttention} need you`
    : `${visibleModules.length} modules`

  return (
    <div id="sb-flyout" style={{
      width: 248, display: 'flex', flexDirection: 'column',
      overflowY: 'auto', overflowX: 'hidden',
      boxShadow: 'inset -6px 0 12px rgba(0,0,0,.18)',
    }}>

      {/* ── Zone 0: Header ─────────────────────────────── */}
      <div style={{
        padding: '16px 14px 14px',
        borderBottom: '1px solid var(--color-sb-bd)',
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: 8,
        flexShrink: 0,
      }}>
        <div>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 15, color: 'var(--color-sb-tx)',
            letterSpacing: '-.01em', lineHeight: 1.2,
          }}>{category.label}</div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 10,
            color: 'var(--color-sb-sec)', marginTop: 3,
          }}>{subtitle}</div>
        </div>
        {/* Collapse button — closes flyout back to rail-only */}
        <button
          onClick={onClose}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          style={{
            width: 28, height: 28, borderRadius: 6, flexShrink: 0,
            background: 'var(--color-sb-hover)', border: 'none',
            cursor: 'pointer', color: 'var(--color-sb-tx2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 120ms', marginTop: 1,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.12)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'var(--color-sb-hover)' }}
        >
          {/* Left-pointing chevron — "collapse" */}
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 4L6 8l4 4"/>
          </svg>
        </button>
      </div>

      {/* ── Zone 1: Modules ────────────────────────────── */}
      <div style={{ paddingBottom: 4 }}>
        <SectionLabel>Modules</SectionLabel>
        {visibleModules.map(item => {
          const icon = MODULE_ICONS[item.pg]
          const count = moduleBadge[item.pg] ?? 0
          const isActive = activeRoute === item.pg
          return (
            <NavLink
              key={item.pg}
              to={`/${item.pg}`}
              onClick={onLinkClick}
              aria-current={isActive ? 'page' : undefined}
              style={({ isActive: active }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 10px',
                borderRadius: 6, margin: '0 4px 2px',
                cursor: 'pointer', textDecoration: 'none',
                position: 'relative',
                background: active ? 'var(--color-sb-active)' : 'transparent',
                transition: 'background 120ms',
              })}
              className="sb-mod-row"
            >
              {({ isActive: active }) => (
                <>
                  {/* Left active marker */}
                  {active && (
                    <span style={{
                      position: 'absolute', left: -4, top: 8, bottom: 8,
                      width: 3, borderRadius: '0 3px 3px 0',
                      background: 'var(--color-sb-ac)',
                    }} />
                  )}
                  {/* Icon chip */}
                  <span style={{
                    width: 22, height: 22, borderRadius: 5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    background: active ? 'rgba(0,216,208,.18)' : 'var(--color-sb-hover)',
                    color: active ? 'var(--color-sb-ac)' : 'var(--color-sb-tx2)',
                    transition: 'background 120ms, color 120ms',
                  }}>
                    {icon && <SBIcon def={icon} size={11} />}
                  </span>
                  {/* Label */}
                  <span style={{
                    flex: 1, fontSize: 12.5,
                    fontWeight: active ? 600 : 500,
                    color: active ? 'var(--color-sb-tx)' : 'var(--color-sb-tx2)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    transition: 'color 120ms',
                  }}>{item.label}</span>
                  {/* Badge */}
                  <ModBadge n={count} />
                </>
              )}
            </NavLink>
          )
        })}
      </div>

      {/* ── Zone 2: Pinned records ──────────────────────── */}
      {(category.pinnedLabel) && (
        <div>
          <SectionLabel>
            {category.pinnedLabel}
          </SectionLabel>

          {combined.length === 0 ? (
            <div style={{
              margin: '0 10px 8px',
              padding: '9px 10px',
              borderRadius: 6,
              background: 'rgba(255,255,255,.025)',
              border: '1px solid var(--color-sb-bd)',
              fontSize: 11, color: 'var(--color-sb-sec)',
              fontFamily: 'var(--font-mono)',
              textAlign: 'center',
            }}>
              No records yet
            </div>
          ) : (
            combined.map(rec => (
              <button
                key={rec.id}
                onClick={() => { navigate(rec.route); onLinkClick?.() }}
                style={{
                  width: 'calc(100% - 20px)', margin: '0 10px 4px',
                  display: 'flex', alignItems: 'flex-start', gap: 9,
                  padding: '9px 10px',
                  borderRadius: 6, cursor: 'pointer', textAlign: 'left',
                  background: 'rgba(255,255,255,.025)',
                  border: '1px solid var(--color-sb-bd)',
                  transition: 'background 120ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.06)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.025)' }}
              >
                {/* Status dot */}
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 4,
                  background: 'var(--color-sb-ac)',
                  boxShadow: '0 0 0 3px rgba(0,216,208,.14)',
                }} />
                <span style={{ overflow: 'hidden' }}>
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: 'var(--color-sb-tx)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{rec.label}</div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10,
                    color: 'var(--color-sb-tx2)', marginTop: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{rec.sub}</div>
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {/* ── Zone 3: Recent (deferred — empty state) ────── */}
      <div>
        <SectionLabel>Recent</SectionLabel>
        <div style={{
          margin: '0 10px 8px',
          fontSize: 11, color: 'var(--color-sb-sec)',
          fontFamily: 'var(--font-mono)',
          textAlign: 'center', padding: '6px 0',
        }}>No recent activity</div>
      </div>

      {/* ── Zone 4: Quick add (pinned to bottom) ─────────
          Navigates to module with state.openAdd = true    */}
      {category.quickActions.length > 0 && (
        <div style={{
          marginTop: 'auto',
          borderTop: '1px solid var(--color-sb-bd)',
          padding: 12, flexShrink: 0,
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 10,
            color: 'var(--color-sb-tx2)', letterSpacing: '.12em',
            textTransform: 'uppercase', marginBottom: 6,
          }}>Quick Add</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 5,
          }}>
            {category.quickActions.map(qa => {
              const missing = qa.depModule ? checkDep(qa.depModule, depData) : null
              const blocked = !!missing
              return (
                <button
                  key={qa.route}
                  onClick={() => {
                    if (blocked) {
                      showToast(`⚠ Add ${missing!.label} first`, 'warning')
                      navigate(`/${missing!.page}`)
                      onLinkClick?.()
                      return
                    }
                    navigate(qa.route, { state: { openAdd: true } })
                    onLinkClick?.()
                  }}
                  title={blocked ? `Requires ${missing!.label}` : qa.label}
                  style={{
                    padding: '7px 8px',
                    borderRadius: 6,
                    cursor: blocked ? 'not-allowed' : 'pointer',
                    background: blocked ? 'rgba(255,255,255,.03)' : 'rgba(0,216,208,.10)',
                    border: blocked ? '1px solid rgba(255,255,255,.07)' : '1px solid rgba(0,216,208,.28)',
                    color: blocked ? 'var(--color-sb-sec)' : 'var(--color-sb-ac)',
                    fontSize: 11, fontWeight: 600,
                    transition: 'background 120ms',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 2, overflow: 'hidden',
                  }}
                  onMouseEnter={blocked ? undefined : e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,216,208,.18)' }}
                  onMouseLeave={blocked ? undefined : e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,216,208,.10)' }}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>{qa.label}</span>
                  {blocked && (
                    <span style={{ fontSize: 9, opacity: .75, whiteSpace: 'nowrap' }}>
                      ⚠ {missing!.label.replace(/^a |^an /, '')} first
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
